import React from "react";
import type { ColumnsType } from "antd/es/table";
import { Localize } from "components/service";
import CommunicationFields from "../fields";
import { formatCommunicationDateTime } from "./communication-date-helper";
import type {
  TCommunicationAuditEntry,
  TCommunicationReferenceOption,
  TCommunicationTab,
  TCommunicationTableLayout,
  TCommunicationTask,
  TCommunicationTaskFormValues,
} from "./communication-types-helper";

interface ICreateCommunicationColumnsProps {
  currentTab: TCommunicationTab;
  layoutMode: TCommunicationTableLayout;
  canEditRaw: boolean;
  canApprove: boolean;
  equipmentOptions: TCommunicationReferenceOption[];
  flooringOptions: TCommunicationReferenceOption[];
  organizerOptions: TCommunicationReferenceOption[];
  exhibitionOptions: TCommunicationReferenceOption[];
  auditEntryMap: Record<string, TCommunicationAuditEntry | undefined>;
  onSave: (taskId: string, values: TCommunicationTaskFormValues) => void;
  onApprove: (taskId: string, values: TCommunicationTaskFormValues) => void;
}

const baseColumns = (layoutMode: TCommunicationTableLayout): ColumnsType<TCommunicationTask> => {
  const isCompactLayout = layoutMode !== "full";
  const isTabletLayout = layoutMode === "tablet";
  const columns: ColumnsType<TCommunicationTask> = [
    {
      title: <Localize>COMMUNICATION.Table_Email</Localize>,
      dataIndex: "sender",
      key: "sender",
      width: isTabletLayout ? 248 : 276,
      sorter: true,
      render: (_, task) => <CommunicationFields.Info task={task} />,
    },
    {
      title: <Localize>COMMUNICATION.Table_EventCompany</Localize>,
      dataIndex: "event_name",
      key: "event_name",
      width: isTabletLayout ? 212 : 228,
      sorter: true,
      render: (_, task) => <CommunicationFields.Overview task={task} />,
    },
    {
      title: <Localize>COMMUNICATION.Table_Stand</Localize>,
      dataIndex: "stand_number",
      key: "stand_number",
      width: isTabletLayout ? 90 : 102,
      sorter: true,
      render: (value: string) => (
        <span className="communication-table__stand-value">{value || "\u2014"}</span>
      ),
    },
    {
      title: <Localize>COMMUNICATION.Table_Changes</Localize>,
      dataIndex: "final_changes",
      key: "final_changes",
      width: isTabletLayout ? 200 : isCompactLayout ? 220 : 240,
      render: (_, task) => <CommunicationFields.Changes task={task} />,
    },
  ];

  if (!isCompactLayout) {
    columns.push({
      title: <Localize>COMMUNICATION.Table_Questions</Localize>,
      dataIndex: "questions",
      key: "questions",
      width: 156,
      sorter: true,
      render: (_, task) => <CommunicationFields.Questions task={task} />,
    });
  }

  return columns;
};

const operationalColumns = (
  props: ICreateCommunicationColumnsProps
): ColumnsType<TCommunicationTask> => {
  const isCompactLayout = props.layoutMode !== "full";
  const isTabletLayout = props.layoutMode === "tablet";
  const columns: ColumnsType<TCommunicationTask> = [ ...baseColumns(props.layoutMode) ];

  if (!isCompactLayout) {
    columns.push({
      title: <Localize>COMMUNICATION.Table_NextStep</Localize>,
      dataIndex: "status",
      key: "status",
      width: 148,
      sorter: true,
      render: (_, task) => <CommunicationFields.NextStep task={task} />,
    });
  }

  columns.push(
    {
      title: <Localize>COMMUNICATION.Table_Received</Localize>,
      dataIndex: "received_at",
      key: "received_at",
      width: isTabletLayout ? 134 : 146,
      sorter: true,
      render: (value: string) => (
        <span className="communication-table__date-value">
          {formatCommunicationDateTime(value) || "\u2014"}
        </span>
      ),
    },
    {
      title: <Localize>COMMUNICATION.Table_Action</Localize>,
      dataIndex: "action",
      key: "action",
      width: isTabletLayout ? 140 : isCompactLayout ? 148 : 168,
      align: "right",
      render: (_, task) => (
        <CommunicationFields.Action
          task={task}
          canEditRaw={props.canEditRaw}
          canApprove={props.canApprove}
          currentTab={props.currentTab}
          equipmentOptions={props.equipmentOptions}
          flooringOptions={props.flooringOptions}
          organizerOptions={props.organizerOptions}
          exhibitionOptions={props.exhibitionOptions}
          auditEntry={props.auditEntryMap[task.id]}
          onSave={props.onSave}
          onApprove={props.onApprove}
        />
      ),
    }
  );

  return columns;
};

const auditColumns = (
  props: ICreateCommunicationColumnsProps
): ColumnsType<TCommunicationTask> => {
  const isCompactLayout = props.layoutMode !== "full";
  const isTabletLayout = props.layoutMode === "tablet";
  const columns: ColumnsType<TCommunicationTask> = [ ...baseColumns(props.layoutMode) ];

  if (!isCompactLayout) {
    columns.push({
      title: <Localize>TABLES.Column_Title_ApprovedBy</Localize>,
      dataIndex: "approved_by",
      key: "approved_by",
      width: 156,
      sorter: true,
      render: (value: string) => (
        <span className="communication-table__user-value">{value || "\u2014"}</span>
      ),
    });
  }

  columns.push(
    {
      title: <Localize>TABLES.Column_Title_ApprovedAt</Localize>,
      dataIndex: "approved_at",
      key: "approved_at",
      width: isTabletLayout ? 138 : 148,
      sorter: true,
      render: (value: string) => (
        <span className="communication-table__date-value">
          {value ? formatCommunicationDateTime(value) : "\u2014"}
        </span>
      ),
    },
    {
      title: <Localize>COMMUNICATION.Table_Action</Localize>,
      dataIndex: "action",
      key: "action",
      width: isTabletLayout ? 128 : isCompactLayout ? 136 : 152,
      align: "right",
      render: (_, task) => (
        <CommunicationFields.Action
          task={task}
          canEditRaw={false}
          canApprove={false}
          currentTab={props.currentTab}
          equipmentOptions={props.equipmentOptions}
          flooringOptions={props.flooringOptions}
          organizerOptions={props.organizerOptions}
          exhibitionOptions={props.exhibitionOptions}
          auditEntry={props.auditEntryMap[task.id]}
          onSave={props.onSave}
          onApprove={props.onApprove}
        />
      ),
    }
  );

  return columns;
};

export const createCommunicationColumns = (
  props: ICreateCommunicationColumnsProps
): ColumnsType<TCommunicationTask> =>
  props.currentTab === "audit" ? auditColumns(props) : operationalColumns(props);
